import type { ReactNode } from "react";
import { ArrowDown, ArrowUp, Minus } from "lucide-react";
import { cn } from "@/lib/cn";
import { Sparkline } from "@/charts/Sparkline";

interface StatCardProps {
  label: string;
  value: ReactNode;
  /** Numeric delta vs comparison period. Sign determines tone. */
  delta?: number;
  /** Format the delta — defaults to "+12.4%". */
  formatDelta?: (delta: number) => string;
  /** When true, a positive delta is bad (e.g. error rate). */
  inverseDelta?: boolean;
  /** Optional sparkline series — small inline trend line. */
  trend?: number[];
  /** Optional caption shown below the metric. */
  hint?: ReactNode;
  className?: string;
  /** Delay (ms) before the sparkline entry animation starts. */
  delay?: number;
}

export function StatCard({
  label,
  value,
  delta,
  formatDelta = defaultFormatDelta,
  inverseDelta,
  trend,
  hint,
  className,
  delay = 0,
}: StatCardProps) {
  const positiveIsGood = !inverseDelta;
  const tone =
    delta == null
      ? "neutral"
      : delta === 0
        ? "neutral"
        : (delta > 0) === positiveIsGood
          ? "positive"
          : "negative";

  return (
    <div
      className={cn(
        "group relative flex flex-col gap-3 rounded-[var(--radius-lg)] border border-border-subtle bg-surface p-4 shadow-[var(--shadow-1)]",
        "transition-[border-color,box-shadow,transform] duration-[var(--duration-default)] ease-[var(--ease-out)]",
        "hover:-translate-y-0.5 hover:border-border-default hover:shadow-[var(--shadow-2)]",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-xs font-medium uppercase tracking-wider text-fg-subtle">
          {label}
        </span>
        {delta != null && (
          <DeltaBadge value={delta} tone={tone} format={formatDelta} />
        )}
      </div>

      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-2xl font-semibold tracking-tight text-fg tabular">
            {value}
          </div>
          {hint && (
            <div className="mt-0.5 truncate text-xs text-fg-muted">{hint}</div>
          )}
        </div>
        {trend && trend.length > 1 && (
          <div className="w-24 shrink-0">
            <Sparkline data={trend} trend={tone} height={36} delay={delay} />
          </div>
        )}
      </div>
    </div>
  );
}

function DeltaBadge({
  value,
  tone,
  format,
}: {
  value: number;
  tone: "positive" | "negative" | "neutral";
  format: (v: number) => string;
}) {
  const Icon = value > 0 ? ArrowUp : value < 0 ? ArrowDown : Minus;
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-0.5 rounded-[var(--radius-sm)] px-1.5 py-0.5 text-[11px] font-medium tabular",
        tone === "positive" && "bg-success-bg text-success-fg",
        tone === "negative" && "bg-danger-bg text-danger-fg",
        tone === "neutral" && "bg-muted text-fg-muted",
      )}
    >
      <Icon className="size-3" />
      {format(value)}
    </span>
  );
}

function defaultFormatDelta(v: number) {
  const abs = Math.abs(v).toFixed(1);
  return `${v > 0 ? "+" : v < 0 ? "−" : ""}${abs}%`;
}
